import { Box, Flex, Text } from 'rebass';
import { FaSyncAlt, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import { keyframes } from '@emotion/core';
import { useLocation } from 'react-router-dom';
import { pluralize } from 'inflected';
import { useDispatch } from 'react-redux';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { clearUploadBatch } from '../actions';
import { useUploadBatchCounts } from './hooks';
import UploadManagerModal from './modal';
import UploadManagerToast from './toast';

const rotate = keyframes`
  from { transform: rotate(0deg) }
  to { transform: rotate(360deg) }
`;

const UploadManager = () => {
  const { pathname } = useLocation();
  const isPublicRoute = pathname.includes('/tell_a_story') || pathname.includes('/get-started')

  const { total, success, error, uploading } = useUploadBatchCounts();
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);

  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    setShowBar(!!total);
  }, [total]);

  const uploadState = useMemo(() => {
    if (!total) return {};
    if (success === total) {
      return { success: true };
    }
    if (error) return { error: true };
    if (total) return { uploading: true };
    return {};
  }, [total, error, success]);

  const uploadMessage = useMemo(() => {
    if (total === 1) {
      if (uploading) {
        return 'Uploading';
      }
      return 'Success';
    }
    return `Uploaded ${success} of ${total}`;
  }, [uploading, success, total]);

  // Telemetry used to live here, derived from the redux batch: a useEffect watched
  // uploadBatch and emitted `*_upload_started` / `*_part_uploaded` /
  // `*_upload_completed` / `*_upload_failed` as the state changed.
  //
  // That is now uploadMedium's job, for two reasons. It emitted from UI state, so
  // the ~13 call sites that never registered in uploadBatch produced no telemetry at
  // all -- which is why production carries stalled media with no upload events. And
  // `*_upload_started` fired when a file was ENQUEUED rather than when its PUT was
  // issued, so a whole batch reported "started" while one file was moving and the
  // event proved nothing about whether a request had been made.
  //
  // This component is now purely the UI.

  useEffect(() => {
    let timer;
    if (uploadState.success) {
      timer = setTimeout(() => setShowBar(false), 2000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [uploadState.success]);

  const showModalRef = useRef(showModal);
  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  useEffect(() => {
    let timer;
    if (uploadState.success) {
      timer = setTimeout(() => !showModalRef.current && dispatch(clearUploadBatch()), 2000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [dispatch, uploadState.success]);

  // Opening the modal hides the toast, so closing it has to decide what to put back.
  //
  // It used to only do setShowModal(false), and the effect that shows the toast runs
  // on `total` alone -- so after closing the details the "Show Details" link was
  // gone with no way to get it back until the count happened to change.
  //
  // It also cleans up after the success timer. That timer skips clearUploadBatch
  // while the modal is open (so the list does not empty out from under someone
  // reading it) and never retries, which left a finished batch sitting in the toast
  // indefinitely. Clearing here zeroes `total`, and the toast hides on its own.
  const closeModal = () => {
    setShowModal(false);

    if (uploadState.success) {
      dispatch(clearUploadBatch());
      return;
    }

    setShowBar(!!total);
  };

  return (
    <>
      <UploadManagerToast
        bg={uploadState.success ? '#e6f3e4' : 'grey20'}
        linkColor={uploadState.success ? 'brand' : 'brandSubtle'}
        p={3}
        // style={isPublicRoute ? {
        //   top: 60,
        //   bottom: 'unset',
        //   left: 0,
        // } : {}}
        isOpen={showBar}
        onClick={() => {
          setShowModal(true);
          setShowBar(false);
          // stop timed clear queue
        }}
      >
        {uploadState.success && (
          <Flex alignItems="center">
            <Box as={FaCheck} mr={2} color="green" />
            <Text fontSize={3} color="grey20">
              {uploadMessage}
            </Text>
          </Flex>
        )}
        {uploadState.error && (
          <Flex alignItems="center">
            <Box as={FaExclamationTriangle} color="noticeIntense" mr={2} />
            <Text color="white" fontSize={3}>
              {uploadMessage}
            </Text>
            <Text color="white" mx={2}>
              &bull;
            </Text>
            <Text color="noticeIntense">
              {error} {pluralize('Error', error)}
            </Text>
          </Flex>
        )}
        {uploadState.uploading && (
          <Flex alignItems="center">
            <Box
              as={FaSyncAlt}
              mr={2}
              color="white"
              css={`
                animation: ${rotate} 2s linear infinite;
              `}
            />
            <Text color="white" fontSize={3}>
              {uploadMessage}
            </Text>
          </Flex>
        )}
      </UploadManagerToast>
      {showModal && <UploadManagerModal onClose={closeModal} />}
    </>
  );
};

export default UploadManager;
